import React, {useCallback, useEffect, useState} from "react";
import {Alert, Box, Button, Grid, useTheme} from "@mui/material";
import MainLayout from "../../components/layouts/MainLayout";
import { FIXED_HEADER_OFFSET } from "../../components/layouts/headerLayout";
import styled from "@emotion/styled";
import {getWorkspacesApi, deleteWorkspaceApi, getWorkspaceDetailApi} from "../../api/workspaceApi";
import {exportCompaniesApi} from "../../api/searchApi";
import {useNavigate} from "react-router-dom";
import ExportReviewDialog from "../../components/ExportReviewDialog";
import DeleteSearchDialog from "../Search/DeleteSearchDialog";
import {buildWorkspaceExportReview, collectWorkspaceCompanyIds, downloadCsvBlob} from "../../helper/csvExport";
import CompanyResultsEmptyState, {
  SAVED_SEARCH_EMPTY,
} from "../../components/EmptyState/CompanyResultsEmptyState";
import {
  deleteMockSavedSearch,
  getMockSavedSearches,
  isMockSavedSearchId,
  isMockSavedSearchesEnabled,
} from "../../fixture/mockSavedSearches";
import SavedSearchList from "./SavedSearchList";
import SavedSearchListBanner from "./SavedSearchListBanner";
import {
  savedSearchListGradientBandSx,
  savedSearchListTableShellSx,
} from "./savedSearchListTableSx";
import {
  getPinnedSavedSearchIds,
  sortSavedSearchesByPin,
} from "../../helper/pinnedSavedSearches";
import {errorDetail} from "../../helper/errorDetail";

const Wrapper = styled(Grid)(() => ({
  padding: `${FIXED_HEADER_OFFSET} 0px 0px 0px`,
  "@media (max-width: 900px)": {
    padding: `${FIXED_HEADER_OFFSET} 0px 0px 0px`,
  },
}));



/** Prefer `id`, then Mongo-style `_id`, then `workspace_id` for list/detail navigation. */
export function resolveWorkspaceListId(workspace) {
  if (!workspace || typeof workspace !== "object") return null;
  const id = workspace.id || workspace._id || workspace.workspace_id;
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

function resolveListedWorkspaces(apiRows) {
  const rows = Array.isArray(apiRows) ? apiRows : [];
  if (rows.length > 0) {
    return sortSavedSearchesByPin(rows, getPinnedSavedSearchIds());
  }
  if (isMockSavedSearchesEnabled()) {
    return sortSavedSearchesByPin(getMockSavedSearches(), getPinnedSavedSearchIds());
  }
  return [];
}

export default function Workspaces() {
  const navigate = useNavigate();
  const theme = useTheme();
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchWorkspaces = useCallback(async () => {
    setLoading(true);
    setListError(null);
    const res = await getWorkspacesApi();
    if (res && res.status === 200) {
      setWorkspaces(resolveListedWorkspaces(res.data));
      if (res.partial) setListError("Some saved searches could not be loaded. Please retry.");
    } else if (isMockSavedSearchesEnabled()) {
      setWorkspaces(resolveListedWorkspaces([]));
    } else {
      setWorkspaces([]);
      setListError("Saved searches could not be loaded. Please retry.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const openDeleteDialog = (id) => {
    const target = workspaces.find((row) => resolveWorkspaceListId(row) === id);
    if (!target) return;
    setDeleteError(null);
    setDeleteTarget({id, name: target.name});
  };

  const handleDelete = async () => {
    const id = deleteTarget?.id;
    if (!id || isDeleting) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      if (isMockSavedSearchId(id)) {
        deleteMockSavedSearch(id);
        setWorkspaces((prev) => prev.filter((row) => resolveWorkspaceListId(row) !== id));
        setDeleteTarget(null);
        return;
      }
      const res = await deleteWorkspaceApi(id);
      if (res && res.status === 200) {
        setDeleteTarget(null);
        await fetchWorkspaces();
      } else {
        setDeleteError(errorDetail(res?.data?.detail, "Failed to delete saved search"));
      }
    } catch (_err) {
      setDeleteError("Failed to delete saved search");
    } finally {
      setIsDeleting(false);
    }
  };

  const [exporting, setExporting] = useState(null);
  const [exportReview, setExportReview] = useState(null);
  const [exportError, setExportError] = useState(null);

  const openExportReview = (workspace) => {
    const workspaceId = resolveWorkspaceListId(workspace);
    if (!workspaceId) return;
    setExportError(null);
    setExportReview({
      workspaceId,
      model: buildWorkspaceExportReview({
        name: workspace.name,
        companyCount: workspace.company_count,
      }),
    });
  };

  const handleExportCsv = async (filename) => {
    if (!exportReview || exporting) return;
    const {workspaceId} = exportReview;
    if (String(workspaceId).startsWith("mock-saved-")) {
      setExportError("Export is unavailable for local preview saved searches.");
      return;
    }
    setExporting(workspaceId);
    setExportError(null);
    try {
      const allIds = await collectWorkspaceCompanyIds(workspaceId, getWorkspaceDetailApi);
      if (!allIds) {
        setExportError("Failed to fetch workspace for export. Try again.");
        return;
      }

      const response = await exportCompaniesApi({ company_ids: allIds, workspace_id: workspaceId });
      
      if (response && response.status === 200) {
        downloadCsvBlob(response.data, filename);
        setExportReview(null);
      } else {
        setExportError("Unable to export this workspace. Try again.");
        if (process.env.NODE_ENV !== 'production') console.error('CSV export failed', response);
      }
    } catch (err) {
      setExportError("Unable to export this workspace. Try again.");
      if (process.env.NODE_ENV !== 'production') console.error('CSV export failed', err);
    } finally {
      setExporting(null);
    }
  };

  const openSavedSearch = useCallback((workspaceOrId) => {
    const id =
      typeof workspaceOrId === "string" || typeof workspaceOrId === "number"
        ? String(workspaceOrId)
        : resolveWorkspaceListId(workspaceOrId);
    if (!id) return;
    navigate(`/workspaces/${id}`);
  }, [navigate]);


  const showSavedSearchEmptyState =
    !loading && !listError && workspaces.length === 0;

  return (
    <MainLayout>
      <Wrapper>
        <SavedSearchListBanner />
        {listError && (
          <Alert severity="warning" action={<Button color="inherit" onClick={fetchWorkspaces}>Retry</Button>}>
            {listError}
          </Alert>
        )}
        <Box sx={savedSearchListGradientBandSx} data-testid="saved-search-list-gradient">
          <Box
            data-testid="saved-search-list-table"
            sx={savedSearchListTableShellSx(theme)}
          >
            {showSavedSearchEmptyState ? (
              <CompanyResultsEmptyState variant={SAVED_SEARCH_EMPTY} />
            ) : (
              <SavedSearchList
                rows={workspaces}
                loading={loading}
                resolveRowId={resolveWorkspaceListId}
                onOpen={openSavedSearch}
                onExport={openExportReview}
                onDelete={openDeleteDialog}
                exportingId={exporting}
              />
            )}
          </Box>
        </Box>
      </Wrapper>
      <ExportReviewDialog
        open={Boolean(exportReview)}
        review={exportReview?.model}
        busy={Boolean(exporting)}
        error={exportError}
        onCancel={() => {
          setExportReview(null);
          setExportError(null);
        }}
        onConfirm={handleExportCsv}
      />
      <DeleteSearchDialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          setDeleteTarget(null);
          setDeleteError(null);
        }}
        onConfirm={handleDelete}
        searchName={deleteTarget?.name}
        isDeleting={isDeleting}
        error={deleteError}
      />
    </MainLayout>
  );
}
